import type { Printer, PrintJobResponse, QueueItem, PrintLogEntry, FontsResponse, Plugin, Driver, NetworkPrinter, PrintManagementResult, PrinterConfig, PrinterConfigResult, DefaultPrinterResponse } from './momentive-print.types'

export class MomentivePrintService {
  private baseUrl: string

  constructor (baseUrl?: string) {
    this.baseUrl = (baseUrl ?? process.env.VUE_APP_MOMENTIVE_PRINT_SERVICE_URL ?? 'http://localhost:8580').replace(/\/$/, '')
  }

  private async request<T> (path: string, init: RequestInit = {}): Promise<T> {
    const resp = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) }
    })
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}))
      throw new Error(body.message ?? body.error ?? `HTTP ${resp.status}`)
    }
    return resp.json()
  }

  getPrinters (): Promise<Printer[]> {
    return this.request('/api/printers')
  }

  print (svg: string, printer: string | undefined, documentName: string, copies = 1, webhookUrl = ''): Promise<PrintJobResponse> {
    return this.request('/api/print', {
      method: 'POST',
      body: JSON.stringify({ svg, ...(printer ? { printer } : {}), documentName, copies, ...(webhookUrl ? { webhookUrl } : {}) })
    })
  }

  getQueue (): Promise<QueueItem[]> {
    return this.request('/api/queue')
  }

  deleteQueueItem (id: string): Promise<void> {
    return this.request('/api/queue', {
      method: 'DELETE',
      body: JSON.stringify({ id })
    })
  }

  getPrintLog (params: { limit?: number; printer?: string; status?: string } = {}): Promise<PrintLogEntry[]> {
    const qs = new URLSearchParams()
    if (params.limit) qs.set('limit', String(params.limit))
    if (params.printer) qs.set('printer', params.printer)
    if (params.status) qs.set('status', params.status)
    const query = qs.toString()
    return this.request('/api/printlog' + (query ? '?' + query : ''))
  }

  getFonts (): Promise<FontsResponse> {
    return this.request('/api/fonts')
  }

  loadFont (params: { url?: string; data?: string }): Promise<void> {
    return this.request('/api/fonts', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  getPlugins (): Promise<Plugin[]> {
    return this.request('/api/plugins')
  }

  remotePrint (params: { printer?: string; url: string; copies?: number; documentType?: string; headers?: Record<string, string> }): Promise<void> {
    return this.request('/api/remoteprint', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  async isRunning (): Promise<boolean> {
    try {
      await this.getPrinters()
      return true
    } catch {
      return false
    }
  }

  getServiceDefaultPrinter (): Promise<DefaultPrinterResponse> {
    return this.request('/api/printmanagement/printers/default')
  }

  async getDefaultPrinter (): Promise<string | null> {
    const printers = await this.getPrinters()
    try {
      const def = await this.getServiceDefaultPrinter()
      // Only trust the stored service default if the printer still exists.
      if (def.isSet && printers.some(p => p.name === def.name)) {
        return def.name
      }
    } catch {
      // PrintManagement plugin not loaded — fall back to OS default
    }
    return printers.find(p => p.isDefault)?.name ?? printers[0]?.name ?? null
  }

  getDrivers (): Promise<Driver[]> {
    return this.request('/api/printmanagement/drivers')
  }

  scanNetwork (params: { subnets?: string[]; community?: string; timeoutMs?: number } = {}): Promise<NetworkPrinter[]> {
    return this.request('/api/printmanagement/scan', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  addPrinter (params: { name: string; ipAddress: string; driverName: string }): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printers', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  renamePrinter (params: { currentName: string; newName: string }): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printers/rename', {
      method: 'PUT',
      body: JSON.stringify(params)
    })
  }

  deletePrinter (name: string): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printers/delete', {
      method: 'DELETE',
      body: JSON.stringify({ name })
    })
  }

  setDefaultPrinter (name: string): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printers/setdefault', {
      method: 'PUT',
      body: JSON.stringify({ name })
    })
  }

  getPrinterConfigs (): Promise<PrinterConfig[]> {
    return this.request('/api/printmanagement/printerconfigs')
  }

  createPrinterConfig (config: Omit<PrinterConfig, 'id'>): Promise<PrinterConfigResult> {
    return this.request('/api/printmanagement/printerconfigs', {
      method: 'POST',
      body: JSON.stringify(config)
    })
  }

  updatePrinterConfig (config: PrinterConfig): Promise<PrinterConfigResult> {
    return this.request('/api/printmanagement/printerconfigs', {
      method: 'POST',
      body: JSON.stringify(config)
    })
  }

  deletePrinterConfig (id: string): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printerconfigs/delete', {
      method: 'DELETE',
      body: JSON.stringify({ id })
    })
  }

  applyPrinterConfig (printerName: string, configId: string): Promise<PrintManagementResult> {
    return this.request('/api/printmanagement/printerconfigs/apply', {
      method: 'POST',
      body: JSON.stringify({ printerName, configId })
    })
  }
}

export default new MomentivePrintService()
